import React, { useState } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Button,
  Popover,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

/**
 * Enterprise-grade FilterBar component
 * Provides advanced filtering, search, and action capabilities
 *
 * @param {Object} props
 * @param {string} props.searchValue - Search input value
 * @param {Function} props.onSearchChange - Handler for search changes
 * @param {string} props.searchPlaceholder - Placeholder for search input
 * @param {Array} props.filters - Array of filter objects [{name, label, options, value, onChange}]
 * @param {Array} props.activeFilters - Array of active filter chips
 * @param {Function} props.onClearFilters - Handler to clear all filters
 * @param {Function} props.onRefresh - Handler for refresh action
 * @param {Function} props.onExport - Handler for export action
 * @param {React.ReactNode} props.customActions - Custom action buttons
 * @param {boolean} props.loading - Loading state
 */
const FilterBar = ({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters = [],
  activeFilters = [],
  onClearFilters,
  onRefresh,
  onExport,
  customActions,
  loading = false,
}) => {
  const theme = useTheme();
  const [advancedFiltersAnchor, setAdvancedFiltersAnchor] = useState(null);

  const handleOpenAdvancedFilters = (event) => {
    setAdvancedFiltersAnchor(event.currentTarget);
  };

  const handleCloseAdvancedFilters = () => {
    setAdvancedFiltersAnchor(null);
  };

  const hasActiveFilters = activeFilters.length > 0;

  return (
    <Box>
      {/* Main Filter Bar */}
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {/* Search Input */}
        {onSearchChange && (
          <TextField
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            size="small"
            sx={{
              minWidth: 280,
              flex: '1 1 auto',
              maxWidth: 400,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchValue && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => onSearchChange('')}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {/* Quick Filters */}
        {filters.slice(0, 3).map((filter, idx) => (
          <FormControl key={filter.name || filter.label || idx} size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{filter.label}</InputLabel>
            {filter.multiple ? (
              <Select
                multiple
                value={filter.value || []}
                onChange={(e) => filter.onChange(e.target.value)}
                input={<OutlinedInput label={filter.label} />}
                renderValue={(selected) =>
                  selected.length === 0
                    ? 'All'
                    : `${selected.length} selected`
                }
              >
                {filter.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox
                      checked={(filter.value || []).indexOf(option.value) > -1}
                      size="small"
                    />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Select
                value={filter.value || ''}
                onChange={(e) => filter.onChange(e.target.value)}
                label={filter.label}
              >
                <MenuItem value="">All</MenuItem>
                {filter.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
        ))}

        {/* Advanced Filters Button */}
        {filters.length > 3 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={handleOpenAdvancedFilters}
          >
            More Filters
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Action Buttons */}
        <Stack direction="row" spacing={1}>
          {onRefresh && (
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={loading}
              title="Refresh"
            >
              <RefreshIcon />
            </IconButton>
          )}

          {onExport && (
            <IconButton
              size="small"
              onClick={onExport}
              disabled={loading}
              title="Export"
            >
              <DownloadIcon />
            </IconButton>
          )}

          {customActions}
        </Stack>
      </Stack>

      {/* Active Filters Chips */}
      {hasActiveFilters && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            mt: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {activeFilters.map((filter, index) => (
            <Chip
              key={index}
              label={`${filter.label}: ${filter.value}`}
              onDelete={filter.onRemove}
              size="small"
              variant="outlined"
            />
          ))}
          {onClearFilters && (
            <Button
              size="small"
              onClick={onClearFilters}
              sx={{ ml: 1 }}
            >
              Clear All
            </Button>
          )}
        </Stack>
      )}

      {/* Advanced Filters Popover */}
      <Popover
        open={Boolean(advancedFiltersAnchor)}
        anchorEl={advancedFiltersAnchor}
        onClose={handleCloseAdvancedFilters}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            p: 3,
            minWidth: 400,
          },
        }}
      >
        <Stack spacing={2}>
          {filters.slice(3).map((filter, idx) => (
            <FormControl key={filter.name || filter.label || idx} size="small" fullWidth>
              <InputLabel>{filter.label}</InputLabel>
              {filter.multiple ? (
                <Select
                  multiple
                  value={filter.value || []}
                  onChange={(e) => filter.onChange(e.target.value)}
                  input={<OutlinedInput label={filter.label} />}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? 'All'
                      : `${selected.length} selected`
                  }
                >
                  {filter.options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox
                        checked={(filter.value || []).indexOf(option.value) > -1}
                        size="small"
                      />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Select
                  value={filter.value || ''}
                  onChange={(e) => filter.onChange(e.target.value)}
                  label={filter.label}
                >
                  <MenuItem value="">All</MenuItem>
                  {filter.options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>
          ))}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleCloseAdvancedFilters}>
              Close
            </Button>
            {onClearFilters && (
              <Button variant="outlined" onClick={() => {
                onClearFilters();
                handleCloseAdvancedFilters();
              }}>
                Clear Filters
              </Button>
            )}
          </Stack>
        </Stack>
      </Popover>
    </Box>
  );
};

export default FilterBar;
